import React, { useState, useMemo, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { EVENT_TYPE_COLORS, getEventTypeName } from '../../constants/notificationTypes';
import { FaCalendarAlt, FaTimes, FaExclamationTriangle } from 'react-icons/fa';

const MultiDayEventModal = ({ 
  selectedDates = [], 
  onSave, 
  onCancel,
  isOpen = true 
}) => {
  const { theme } = useTheme();
  
  // ✅ SEMPLIFICATO: Solo campi database supportati
  const [eventData, setEventData] = useState({
    titolo: '',
    descrizione: '',
    luogo: '',
    tipoEvento: '', // Sarà popolato da EVENT_TYPE_COLORS
    giornataIntera: true // Sempre true per eventi multi-giorno
  });
  
  const [errors, setErrors] = useState({});
  const [tipiEventi, setTipiEventi] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);

  // ✅ FIX: Usa gli stessi tipi della modale normale
  useEffect(() => {
    const caricaTipiEventi = () => {
      console.log('🔄 Caricamento tipi eventi da EVENT_TYPE_COLORS...');
      setIsLoadingTypes(true);
      
      try {
        // ✅ CONVERSIONE: Da EVENT_TYPE_COLORS al formato atteso
        const tipiFromConstants = Object.entries(EVENT_TYPE_COLORS).map(([id, config]) => ({
          TEV_Id_TipiEventi: parseInt(id),
          TEV_Descrizione: config.descrizione,
          TEV_Codice: config.codice
        }));
        
        console.log('📋 Tipi eventi da EVENT_TYPE_COLORS:', tipiFromConstants);
        setTipiEventi(tipiFromConstants);
        
        // ✅ Imposta tipo default (FERIE per eventi multi-giorno)
        const ferieType = tipiFromConstants.find(tipo => tipo.TEV_Codice === 'FERIE');
        if (ferieType) {
          setEventData(prev => ({
            ...prev,
            tipoEvento: ferieType.TEV_Id_TipiEventi
          }));
          console.log('✅ Tipo evento default (FERIE):', ferieType.TEV_Id_TipiEventi);
        } else if (tipiFromConstants.length > 0) {
          setEventData(prev => ({
            ...prev,
            tipoEvento: tipiFromConstants[0].TEV_Id_TipiEventi
          }));
          console.log('✅ Tipo evento default (primo):', tipiFromConstants[0].TEV_Id_TipiEventi);
        }
        
      } catch (error) {
        console.error('❌ Errore caricamento tipi eventi:', error);
        // ✅ FALLBACK: Usa tipi hardcoded compatibili
        const tipiDefault = [
          { TEV_Id_TipiEventi: 2, TEV_Descrizione: 'Ferie', TEV_Codice: 'FERIE' },
          { TEV_Id_TipiEventi: 3, TEV_Descrizione: 'Permessi', TEV_Codice: 'PERMESSI' },
          { TEV_Id_TipiEventi: 1, TEV_Descrizione: 'Sede Arezzo', TEV_Codice: 'SEDE' },
          { TEV_Id_TipiEventi: 4, TEV_Descrizione: 'Web Meeting', TEV_Codice: 'WEBMEETING' },
          { TEV_Id_TipiEventi: 5, TEV_Descrizione: 'Riunione', TEV_Codice: 'RIUNIONE' }
        ];
        console.log('🔧 Usando tipi eventi di fallback:', tipiDefault);
        setTipiEventi(tipiDefault);
        setEventData(prev => ({
          ...prev,
          tipoEvento: tipiDefault[0].TEV_Id_TipiEventi
        }));
      } finally {
        setIsLoadingTypes(false);
      }
    };

    // ✅ SEMPRE carica i tipi evento quando il componente è montato
    caricaTipiEventi();
  }, []); // ✅ RIMOSSO isOpen dependency

  // ✅ AGGIUNTO: Debug per vedere quando il modal si apre/chiude
  useEffect(() => {
    console.log('📝 MultiDayEventModal state:', { 
      isOpen, 
      selectedDatesCount: selectedDates.length,
      tipiEventiCount: tipiEventi.length,
      currentTipoEvento: eventData.tipoEvento,
      isLoadingTypes
    });
  }, [isOpen, selectedDates, tipiEventi, eventData.tipoEvento, isLoadingTypes]);

  // ✅ MANTIENE: Analisi date helper functions
  const isConsecutiveRange = (dates) => {
    if (dates.length <= 1) return true;
    
    const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
    
    for (let i = 1; i < sortedDates.length; i++) {
      const prevDate = sortedDates[i - 1];
      const currentDate = sortedDates[i];
      const diffTime = currentDate.getTime() - prevDate.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      
      if (diffDays !== 1) return false;
    }
    
    return true;
  };

  const findDateGaps = (dates) => {
    if (dates.length <= 1) return [];
    
    const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
    const gaps = [];
    
    for (let i = 1; i < sortedDates.length; i++) {
      const prev = sortedDates[i - 1];
      const curr = sortedDates[i];
      const diffTime = curr.getTime() - prev.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      
      if (diffDays > 1) {
        gaps.push({
          after: prev,
          before: curr,
          daysGap: diffDays - 1
        });
      }
    }
    return gaps;
  };

  // ✅ MANTIENE: Analisi date
  const dateAnalysis = useMemo(() => {
    if (!selectedDates.length) return null;
    
    const sortedDates = [...selectedDates].sort((a, b) => a.getTime() - b.getTime());
    const weekendDays = sortedDates.filter(date => date.getDay() === 0 || date.getDay() === 6);
    const workDays = sortedDates.filter(date => date.getDay() !== 0 && date.getDay() !== 6);
    
    return {
      totalDays: selectedDates.length,
      workDays: workDays.length,
      weekendDays: weekendDays.length,
      startDate: sortedDates[0],
      endDate: sortedDates[sortedDates.length - 1],
      isConsecutive: isConsecutiveRange(sortedDates),
      gaps: findDateGaps(sortedDates)
    };
  }, [selectedDates]);

  const formatDateRange = () => {
    if (!dateAnalysis) return '';
    
    const { startDate, endDate, totalDays } = dateAnalysis;
    
    if (totalDays === 1) {
      return startDate.toLocaleDateString('it-IT');
    }
    
    return `${startDate.toLocaleDateString('it-IT')} - ${endDate.toLocaleDateString('it-IT')}`;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!eventData.titolo.trim()) {
      newErrors.titolo = 'Il titolo è obbligatorio';
    }
    
    if (!eventData.tipoEvento) {
      newErrors.tipoEvento = 'Seleziona un tipo evento';
    }
    
    if (selectedDates.length === 0) {
      newErrors.dates = 'Seleziona almeno una data';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    console.log('💾 Tentativo di salvataggio:', { eventData, selectedDates, tipiEventi });
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      // ✅ HELPER: Funzione per convertire in formato MySQL
      const toMySQLDateTime = (date) => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
          return null;
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      // ✅ CORRETTO: Prepara dati con formato MySQL come QuickCreateModal
      const eventsToCreate = selectedDates.map(date => {
        // ✅ IMPORTANTE: CAL_GiornataIntera = true per eventi multi-giorno
        const startDate = new Date(date);
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(date);
        endDate.setHours(23, 59, 59, 999);
        
        return {
          CAL_Titolo: eventData.titolo,
          CAL_Descrizione: eventData.descrizione,
          CAL_Luogo: eventData.luogo || null,
          CAL_DataOraInizio: toMySQLDateTime(startDate), // ✅ FORMATO MYSQL
          CAL_DataOraFine: toMySQLDateTime(endDate),     // ✅ FORMATO MYSQL
          CAL_GiornataIntera: 1, // ✅ SEMPRE TRUE per ferie/multi-day (come numero)
          CAL_Id_TipiEventi: parseInt(eventData.tipoEvento),
          CAL_Privato: false, // ✅ Eventi pubblici per ferie
          CAL_Partecipanti: [] // ✅ Array vuoto invece di stringa
        };
      });
      
      console.log('📅 Eventi da creare (formato MySQL):', eventsToCreate);
      
      // ✅ Salva eventi
      await onSave({
        events: eventsToCreate,
        analysis: dateAnalysis
      });
      
    } catch (error) {
      console.error('Errore salvataggio evento multi-giorno:', error);
      setErrors({ submit: 'Errore durante il salvataggio' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const mutedText = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const inputBg = theme === 'dark' ? 'bg-gray-700' : 'bg-white';
  const borderColor = theme === 'dark' ? 'border-gray-600' : 'border-gray-300';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${modalBg} rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FaCalendarAlt className="text-emerald-600 text-xl" />
            <div>
              <h2 className={`text-xl font-bold ${textColor}`}>
                Evento Multi-Giorno
              </h2>
              <p className={`text-sm ${mutedText}`}>
                {dateAnalysis?.totalDays} giorni selezionati
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors ${mutedText}`}
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSave} className="space-y-6">
            {/* Date Analysis */}
            {dateAnalysis && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <h3 className={`font-semibold ${textColor} mb-2`}>Riepilogo Date</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className={`font-medium ${textColor}`}>Periodo:</span>
                    <p className={mutedText}>{formatDateRange()}</p>
                  </div>
                  <div>
                    <span className={`font-medium ${textColor}`}>Totale giorni:</span>
                    <p className={mutedText}>
                      {dateAnalysis.totalDays} 
                      {dateAnalysis.workDays > 0 && ` (${dateAnalysis.workDays} lavorativi)`}
                    </p>
                  </div>
                </div>

                {/* Warnings */}
                {dateAnalysis.weekendDays > 0 && (
                  <div className="mt-3 flex items-center gap-2 text-amber-600 dark:text-amber-400">
                    <FaExclamationTriangle className="w-4 h-4" />
                    <span className="text-sm">
                      Include {dateAnalysis.weekendDays} giorni weekend
                    </span>
                  </div>
                )}

                {!dateAnalysis.isConsecutive && (
                  <div className="mt-2 flex items-center gap-2 text-orange-600 dark:text-orange-400">
                    <FaExclamationTriangle className="w-4 h-4" />
                    <span className="text-sm">
                      Date non consecutive - {dateAnalysis.gaps.length} interruzioni
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ✅ FORM SEMPLIFICATO */}
            <div className="space-y-4">
              {/* Titolo */}
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-2`}>
                  Titolo *
                </label>
                <input
                  type="text"
                  value={eventData.titolo}
                  onChange={(e) => setEventData({...eventData, titolo: e.target.value})}
                  placeholder={`Ferie - ${formatDateRange()}`}
                  className={`
                    w-full px-3 py-2 border rounded-lg ${inputBg} ${borderColor} ${textColor}
                    focus:outline-none focus:ring-2 focus:ring-emerald-500
                    ${errors.titolo ? 'border-red-500' : ''}
                  `}
                />
                {errors.titolo && (
                  <p className="text-red-500 text-sm mt-1">{errors.titolo}</p>
                )}
              </div>

              {/* ✅ Tipo Evento compatibile con modale normale */}
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-2`}>
                  Tipo Evento *
                </label>
                {isLoadingTypes ? (
                  <div className={`w-full px-3 py-2 border rounded-lg ${inputBg} ${borderColor} ${textColor}`}>
                    Caricamento tipi evento...
                  </div>
                ) : (
                  <select
                    value={eventData.tipoEvento}
                    onChange={(e) => setEventData({...eventData, tipoEvento: e.target.value})}
                    className={`
                      w-full px-3 py-2 border rounded-lg ${inputBg} ${borderColor} ${textColor}
                      focus:outline-none focus:ring-2 focus:ring-emerald-500
                      ${errors.tipoEvento ? 'border-red-500' : ''}
                    `}
                  >
                    <option value="">Seleziona tipo evento...</option>
                    {tipiEventi.map(tipo => (
                      <option key={tipo.TEV_Id_TipiEventi} value={tipo.TEV_Id_TipiEventi}>
                        {tipo.TEV_Descrizione}
                      </option>
                    ))}
                  </select>
                )}
                {errors.tipoEvento && (
                  <p className="text-red-500 text-sm mt-1">{errors.tipoEvento}</p>
                )}
              </div>

              {/* Descrizione */}
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-2`}>
                  Descrizione
                </label>
                <textarea
                  value={eventData.descrizione}
                  onChange={(e) => setEventData({...eventData, descrizione: e.target.value})}
                  placeholder="Dettagli aggiuntivi, contatti emergenza..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg ${inputBg} ${borderColor} ${textColor} focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                />
              </div>

              {/* Luogo */}
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-2`}>
                  Luogo
                </label>
                <input
                  type="text"
                  value={eventData.luogo}
                  onChange={(e) => setEventData({...eventData, luogo: e.target.value})}
                  placeholder="Es. Mare, Montagna, Casa..."
                  className={`w-full px-3 py-2 border rounded-lg ${inputBg} ${borderColor} ${textColor} focus:outline-none focus:ring-2 focus:ring-emerald-500`}
                />
              </div>
            </div>

            {/* ✅ Info giornata intera */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
              <p className={`text-sm ${textColor}`}>
                ✅ Giornata intera abilitata automaticamente per eventi multi-giorno
              </p>
            </div>

            {errors.submit && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-red-600 dark:text-red-400 text-sm">{errors.submit}</p>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700">
          <div className={`text-sm ${mutedText}`}>
            {dateAnalysis?.workDays > 0 && 
              `${dateAnalysis.workDays} giorni lavorativi`
            }
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50"
            >
              {isLoading ? 'Salvataggio...' : `Salva ${dateAnalysis?.totalDays} Giorni`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiDayEventModal;